import {css} from 'aphrodite';
import React, {useRef} from 'react';
import _ from 'underscore';

import {sharedStyles} from 'processes/settings/fields/ProcessTaskSchemaFieldConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {EntityWithTextAndSuspended, MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';
import {EntityInputWithSearch, UserSearchType} from 'ui-library/entity-input/EntityInputWithSearch';
import ErrorMessage from 'ui-library/ErrorMessage';
import EditablePartyField from 'ui-library/inputs/EditablePartyField';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';

interface DefaultResponsibleTeamFieldProps {
  value: MinimalEntity | null;
  onChange: (value: MinimalEntity | null) => void;
  isBeingCreated: boolean;
  label: string;
  setIsDefaultResponsibleTeamValid: (value: boolean) => void;
  showError: boolean;
  isDisabled?: boolean;
  dataTestAttrs?: DataTestAttrs;
}

export function DefaultResponsibleTeamField(props: DefaultResponsibleTeamFieldProps): JSX.Element {
  const componentId = useRef(_.uniqueId());
  const entityToMinimalEntity = (entity?: EntityWithTextAndSuspended): MinimalEntity | null =>
    entity
      ? {
          id: entity.id,
          handle: entity.handle,
        }
      : null;

  const sharedInputProps = {
    enableNewEntityInput: true,
    allowEmails: false,
    allowTeams: true,
    allowOrganizations: false,
    placeholder: 'Search for team',
    shouldAllowMultiple: false as const, // explicitly casting to false because PartyProps expects false (not boolean)
    userSearchType: UserSearchType.None,
  };

  return (
    <>
      <InputWrapper
        id={componentId.current}
        label={props.label}
        labelStyle={FieldLabel.STYLES.LABEL}
        isRequired={false}
      >
        {props.isBeingCreated ? (
          <EntityInputWithSearch
            {...sharedInputProps}
            onChange={(assigneeArray: Array<EntityWithTextAndSuspended>) => {
              props.onChange(entityToMinimalEntity(assigneeArray[0]));
            }}
            onChangeInput={(value) => {
              props.setIsDefaultResponsibleTeamValid(value === '');
            }}
            value={props.value ? [props.value] : []}
            size={TagsInputSize.SMALL}
            className={css(sharedStyles.input)}
            emptyText="No results found"
          />
        ) : (
          <EditablePartyField
            {...sharedInputProps}
            isFullWidth={true}
            value={props.value}
            initialIsBeingEdited={false}
            isDisabled={Boolean(props.isDisabled)}
            onChange={(changedValue: MinimalEntity) => {
              props.onChange(changedValue);
            }}
            allowEmptyValue={true}
            placeholder="Search for team"
            chipSize={TagsInputSize.SMALL}
            dataTestAttrs={props.dataTestAttrs}
          />
        )}
      </InputWrapper>
      {props.showError && <ErrorMessage error='Invalid "Default responsible team" value' />}
    </>
  );
}
